import { IdSchemaEnum } from "../../../Configurator/Enums/IdSchemaEnum";
import SDKConfigurationModel from "../../../Configurator/Models/SDKConfiguraitonModel";
import { ErrorCodes } from "../../../Exception/ErrorCodes";
import { ErrorStatuses } from "../../../Exception/ErrorStatuses";
import { ErrorMessages } from "../../../Global/Messages/Messages";
import ClientHttps, { FeatureConfigEnum } from "../../../Https/ClientHttps";
import FootballHttps from "../../../Https/FootballHttps";
import LoyaltyHttps from "../../../Https/LoyaltyHttps";
import PredictorHttps from "../../../Https/PredictorHttps";
import PredictionsFilters from "../../Predictor/Models/Predictions/PredictionsFilters";
import FantasyFactory from "../Factory/FantasyFactory";
import FantasyConfigModel from "../Models/Config/FantasyConfigModel";
import FantasyPredictionRequestBody from "../Models/FantasyPredictionRequestBody";
import FantasyService from "../Service/FantasyService";

export default class FantasyFacade {
  private idSchema: string = null;
  private clientHttps: ClientHttps = null;
  private footballHttps: FootballHttps = null;
  private predictorHttps: PredictorHttps = null;
  private loyaltyHttps: LoyaltyHttps = null;
  private service: FantasyService = null;

  constructor(config: SDKConfigurationModel) {
    this.idSchema = config.idSchema;
    this.clientHttps = new ClientHttps(config);
    this.footballHttps = new FootballHttps(config);
    this.service = new FantasyService(config);
    this.predictorHttps = new PredictorHttps(config);
    this.loyaltyHttps = new LoyaltyHttps(config);
  }

  public getConfig = async () => {
    const config = (await this.clientHttps.getConfig(
      FeatureConfigEnum.FANTASY
    )) as FantasyConfigModel;

    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      config.competitionsWhitelist = await this.service.remapCompetitions([
        ...config.competitionsWhitelist,
      ]);
    }

    return config;
  };

  public getPlayersPoints = async (
    templateId: string,
    playersPosition: string,
    disableCache: boolean
  ) => {
    const response = await this.footballHttps.getPlayersPoints(
      templateId,
      playersPosition,
      disableCache
    );
    response.data = await this.service.addPlayerModels(response.data);

    return response;
  };

  public getPlayerDetailedStatistics = async (
    templateId: string,
    playerId: string,
    disableCache: boolean
  ) => {
    let nativePlayerId = "";

    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      nativePlayerId = await this.service.remapPlayerIdToNative(playerId);
    }
    const response = await this.footballHttps.getPlayerDetailedStatistics(
      templateId,
      nativePlayerId || playerId,
      disableCache
    );
    response.meta.filters.playerId = playerId;
    response.data = await this.service.addMatchModels(response.data);

    return response;
  };

  public play = async (
    templateId: string,
    groupId: string,
    playerIds: string[],
    playersRoles: Map<string, string> | null
  ) => {
    if (!playerIds || !Array.isArray(playerIds)) {
      this.service.throwException(
        ErrorCodes.BAD_METHOD_CALL,
        ErrorStatuses.INVALID_METHOD_CALL,
        ErrorMessages.INVALID_PARAM + "playerIds"
      );
    }

    if (playersRoles) {
      const property = "playersRoles";
      if (!(playersRoles instanceof Map)) {
        this.service.throwException(
          ErrorCodes.BAD_METHOD_CALL,
          ErrorStatuses.INVALID_METHOD_CALL,
          ErrorMessages.INVALID_PARAM + property
        );
      }

      const rolesPlayerIds = Array.from(playersRoles.keys());

      if (!rolesPlayerIds.length) {
        this.service.throwException(
          ErrorCodes.BAD_METHOD_CALL,
          ErrorStatuses.INVALID_METHOD_CALL,
          ErrorMessages.INVALID_PARAM + property
        );
      }

      const isPlayerIdNotInList = rolesPlayerIds.every(
        (id) => !playerIds.includes(id)
      );

      if (isPlayerIdNotInList) {
        this.service.throwException(
          ErrorCodes.BAD_METHOD_CALL,
          ErrorStatuses.INVALID_METHOD_CALL,
          ErrorMessages.INVALID_MAP_PLAYERS_ROLES_KEYS + playerIds.join(",")
        );
      }

      const roles = Array.from(playersRoles.values());
      const validRoles = ["CAPTAIN", "VICE_CAPTAIN"];

      if (
        !roles.every((role) => validRoles.includes(role.toLocaleUpperCase()))
      ) {
        this.service.throwException(
          ErrorCodes.BAD_METHOD_CALL,
          ErrorStatuses.INVALID_METHOD_CALL,
          ErrorMessages.INVALID_MAP_PLAYERS_ROLES_VALUES + validRoles.join(",") + ". Case insensitive."
        );
      }
    }

    let requestBody: FantasyPredictionRequestBody = null;

    if (this.idSchema !== IdSchemaEnum.NATIVE) {
      const nativePlayerIds = await this.service.remapPlayers(
        playerIds,
        this.idSchema,
        IdSchemaEnum.NATIVE,
        playersRoles
      );
      requestBody = FantasyFactory.createFantasyPrediction(
        templateId,
        groupId,
        nativePlayerIds,
        playersRoles
      );
    } else {
      requestBody = FantasyFactory.createFantasyPrediction(
        templateId,
        groupId,
        playerIds,
        playersRoles
      );
    }

    return await this.predictorHttps.makeFootballPrediction(requestBody);
  };

  public getMyPrediction = async (templateId: string, groupId: string) => {
    const gameId = `${templateId}_${groupId}`;
    const filters = new PredictionsFilters(null);
    filters.gameIds = [gameId];

    const response = await this.predictorHttps.getMyPredictions(filters);

    if (response.data.length === 0) {
      return null;
    }

    return response.data[0];
  };

  public getPlayerStatistics = async (
    templateId: string,
    playerId: string,
    disableCache?: boolean
  ) => {
    const detailedStats = await this.getPlayerDetailedStatistics(
      templateId,
      playerId,
      disableCache
    );

    return FantasyFactory.createFantasyPlayerStats(detailedStats);
  };

  public getGameWeekStatistics = async (templateId: string) => {
    return await this.loyaltyHttps.getTemplateBreakdownPoints(templateId);
  };
}
